import React, { useState } from 'react';
import type { PlanningItemDto } from './ProjectPlanningPage';

interface CreatePlanningItemModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (item: PlanningItemDto) => void;
  projectId: string;
  existingItems: PlanningItemDto[];
  maxLevel?: number;
  editingItem?: PlanningItemDto | null;
}

function collectDescendantIds(parentId: string, items: PlanningItemDto[]): string[] {
  const ids: string[] = [];
  for (const i of items) {
    if (i.parent_id === parentId) {
      ids.push(i.id, ...collectDescendantIds(i.id, items));
    }
  }
  return ids;
}

export default function CreatePlanningItemModal({
  open,
  onClose,
  onSuccess,
  projectId,
  existingItems,
  maxLevel = 3,
  editingItem = null,
}: CreatePlanningItemModalProps) {
  const [name, setName] = useState('');
  const [durationDays, setDurationDays] = useState<number | ''>(1);
  const [budgetAmount, setBudgetAmount] = useState<number | ''>(0);
  const [parentId, setParentId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      if (editingItem) {
        setName(editingItem.name);
        setDurationDays(editingItem.duration_days);
        setBudgetAmount(editingItem.budget_amount);
        setParentId(editingItem.parent_id || '');
        setNotes(editingItem.notes || '');
      } else {
        setName('');
        setDurationDays(1);
        setBudgetAmount(0);
        setParentId('');
        setNotes('');
      }
      setError(null);
    }
  }, [open, editingItem]);

  const getItemLevel = (item: PlanningItemDto): number => {
    if (!item.parent_id) return 1;
    const parent = existingItems.find((p) => p.id === item.parent_id);
    if (!parent) return 2;
    return parent.parent_id ? 3 : 2;
  };

  const descendantsOfEditing = editingItem ? new Set([editingItem.id, ...collectDescendantIds(editingItem.id, existingItems)]) : new Set<string>();
  const availableParents = existingItems.filter((item) => {
    if (getItemLevel(item) >= maxLevel) return false;
    if (editingItem && descendantsOfEditing.has(item.id)) return false;
    return true;
  });

  const resetForm = () => {
    setName('');
    setDurationDays(1);
    setBudgetAmount(0);
    setParentId('');
    setNotes('');
    setError(null);
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Item name is required.');
      return;
    }
    const days = typeof durationDays === 'number' ? durationDays : parseInt(String(durationDays), 10) || 0;
    if (days < 0) {
      setError('Duration must be 0 or more days.');
      return;
    }
    const budget = typeof budgetAmount === 'number' ? budgetAmount : parseFloat(String(budgetAmount)) || 0;
    if (budget < 0) {
      setError('Budget amount cannot be negative.');
      return;
    }

    if (editingItem) {
      const updated: PlanningItemDto = {
        ...editingItem,
        name: name.trim(),
        duration_days: days,
        budget_amount: budget,
        parent_id: parentId || null,
        notes: notes.trim() || null,
      };
      onSuccess(updated);
    } else {
      const item: PlanningItemDto = {
        id: `plan-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        project_id: projectId,
        name: name.trim(),
        duration_days: days,
        budget_amount: budget,
        parent_id: parentId || null,
        notes: notes.trim() || null,
        sort_order: existingItems.length,
      };
      onSuccess(item);
    }

    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} aria-hidden="true" />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-200">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{editingItem ? 'Edit Planning Item' : 'Add Planning Item'}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Design phase, Development, Testing"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item (Optional)</label>
            <select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%20%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
            >
              <option value="">— Top level (no parent) —</option>
              {availableParents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.parent_id ? '  ↳ ' : ''}{p.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Supports up to {maxLevel} levels: Parent → Child → Mid-level</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (Days) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                placeholder="0"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">How many days needed to complete this item</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Amount (₱)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₱</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={budgetAmount}
                  onChange={(e) => setBudgetAmount(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full rounded-xl border border-gray-200 bg-white pl-8 pr-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Budget consumed by this item</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={2}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm resize-none"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            {editingItem ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </>
  );
}
